import { useState, useCallback, useEffect } from 'react';
import { v4 as uuid } from 'uuid';
import { AGENTS, AGENT_LIST } from '../constants/agents';
import { resolveAgentSampleKey } from '../constants/agentCatalog';
import { SAMPLES } from '../constants/samples';
import { Agent, AgentState, Message } from '../types/agent';
import { runAgent, fetchAgents, RunAgentResponse } from '../services/api';

type AgentStates = Record<string, AgentState>;

function createEmptyAgentState(): AgentState {
  return {
    status: 'idle',
    messages: [],
    result: null,
    taskCount: 0,
    lastActivity: null,
    processingTime: null,
  };
}

const initialState: AgentStates = Object.fromEntries(
  Object.keys(AGENTS).map((id) => [id, createEmptyAgentState()])
);

function apiAgentToFrontend(a: Record<string, unknown>): Agent {
  const id = (a.agent_id || a.id || '') as string;
  return {
    id,
    name: (a.name || '') as string,
    subtitle: (a.subtitle || a.description || '') as string,
    icon: (a.icon || id.slice(0, 2).toUpperCase()) as string,
    color: (a.color || '#888888') as string,
    glow: (a.glow || `rgba(128,128,128,0.2)`) as string,
    description: (a.description || '') as string,
    capabilities: Array.isArray(a.capabilities) ? a.capabilities as string[] : [],
    status: 'idle',
    isTemplate: !!(a.is_template),
    isDefault: !!(a.is_default),
  };
}

export function useAgents() {
  const [agents, setAgents] = useState<Agent[]>(AGENT_LIST);
  const [agentStates, setAgentStates] = useState<AgentStates>(initialState);
  const [runAllProgress, setRunAllProgress] = useState<string | null>(null);

  const refreshAgents = useCallback(async () => {
    try {
      const { agents: apiAgents } = await fetchAgents();
      const mapped = apiAgents.map(apiAgentToFrontend);
      if (mapped.length > 0) {
        setAgents(mapped);
        setAgentStates(prev => {
          const next = { ...prev };
          for (const a of mapped) {
            if (!next[a.id]) {
              next[a.id] = createEmptyAgentState();
            }
          }
          return next;
        });
      }
    } catch { /* ignore */ }
  }, []);

  // Fetch agents from API on mount
  useEffect(() => {
    refreshAgents().catch(() => {});
    const interval = setInterval(() => {
      refreshAgents().catch(() => {});
    }, 10000);
    return () => clearInterval(interval);
  }, [refreshAgents]);

  const addMessage = useCallback((agentId: string, message: Message) => {
    setAgentStates((prev) => ({
      ...prev,
      [agentId]: {
        ...(prev[agentId] || createEmptyAgentState()),
        messages: [...((prev[agentId]?.messages) || []), message],
        lastActivity: message.timestamp,
      },
    }));
  }, []);

  const executeAgent = useCallback(async (agentId: string, inputText?: string): Promise<RunAgentResponse | null> => {
    const input = inputText && inputText.trim() ? inputText : SAMPLES[resolveAgentSampleKey(agentId)] || '';

    addMessage(agentId, { id: uuid(), type: 'user', content: input, timestamp: new Date().toISOString() });

    setAgentStates((prev) => ({
      ...prev,
      [agentId]: { ...(prev[agentId] || createEmptyAgentState()), status: 'processing' },
    }));

    try {
      const response = await runAgent(agentId, input);
      addMessage(agentId, {
        id: uuid(),
        type: 'agent',
        content: typeof response.result.summary === 'string' ? response.result.summary : 'Execution complete',
        timestamp: response.timestamp,
        result: response.result,
        processingTime: response.processingTime,
        model: response.model,
      });

      setAgentStates((prev) => ({
        ...prev,
        [agentId]: {
          ...(prev[agentId] || createEmptyAgentState()),
          status: 'complete',
          result: response.result,
          taskCount: Array.isArray(response.result.tasks) ? response.result.tasks.length : prev[agentId]?.taskCount || 0,
          processingTime: response.processingTime,
        },
      }));
      return response;
    } catch (error) {
      addMessage(agentId, {
        id: uuid(),
        type: 'error',
        content: error instanceof Error ? error.message : 'Agent execution failed',
        timestamp: new Date().toISOString(),
      });
      setAgentStates((prev) => ({
        ...prev,
        [agentId]: { ...(prev[agentId] || createEmptyAgentState()), status: 'error' },
      }));
      return null;
    }
  }, [addMessage]);

  const runAllAgents = useCallback(async () => {
    const runnableAgents = agents.filter((agent) => !agent.isTemplate);
    for (let i = 0; i < runnableAgents.length; i += 1) {
      const agent = runnableAgents[i];
      setRunAllProgress(`Running ${agent.name} (${i + 1}/${runnableAgents.length})`);
      await executeAgent(agent.id, SAMPLES[resolveAgentSampleKey(agent.id)]);
    }
    setRunAllProgress(null);
  }, [agents, executeAgent]);

  const isAnyProcessing = Object.values(agentStates).some((s) => s.status === 'processing');
  const totalTasksCreated = Object.values(agentStates).reduce((sum, s) => sum + s.taskCount, 0);

  return { agents, agentStates, executeAgent, runAllAgents, runAllProgress, isAnyProcessing, totalTasksCreated, addMessage, refreshAgents };
}
